import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, MessageCircle, ShieldCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modale de vérification du numéro par OTP (in-page).
// onVerified(phone) appelé après succès.
export const PhoneOtpModal = ({ open, onClose, defaultPhone = '', onVerified }) => {
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [phone, setPhone] = useState(defaultPhone);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Sync le numéro pré-rempli quand la modale s'ouvre ou que le profil se charge.
  useEffect(() => {
    if (open) { setPhone(defaultPhone || ''); setStep('phone'); setCode(''); }
  }, [open, defaultPhone]);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` } });

  const reset = () => { setStep('phone'); setCode(''); setSending(false); setVerifying(false); };
  const handleClose = () => { reset(); onClose?.(); };

  const sendOtp = async () => {
    const p = (phone || '').trim();
    if (!p) { toast.error('Saisissez votre numéro'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/phone-verification/send-otp`, { phone: p }, auth());
      toast.success('Code envoyé par WhatsApp');
      setStep('code');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi du code");
    }
    setSending(false);
  };

  const verifyOtp = async () => {
    const c = (code || '').replace(/\D/g, '');
    if (c.length !== 4) { toast.error('Le code contient 4 chiffres'); return; }
    setVerifying(true);
    try {
      await axios.post(`${API}/phone-verification/verify-otp`, { phone: phone.trim(), otp_code: c }, auth());
      toast.success('Numéro vérifié ✅');
      onVerified?.(phone.trim());
      handleClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Code invalide');
    }
    setVerifying(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="phone-otp-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Vérifier mon numéro
          </DialogTitle>
          <DialogDescription>
            {step === 'phone'
              ? 'Recevez un code de vérification par WhatsApp pour activer les notifications.'
              : `Saisissez le code à 4 chiffres envoyé au ${phone}.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'phone' ? (
          <div className="space-y-3">
            <Label htmlFor="otp-phone">Numéro de téléphone</Label>
            <Input id="otp-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+225 0700000000" data-testid="otp-phone-input"
              onKeyDown={(e) => e.key === 'Enter' && sendOtp()} />
          </div>
        ) : (
          <div className="space-y-3">
            <Label htmlFor="otp-code">Code de vérification</Label>
            <Input id="otp-code" value={code} inputMode="numeric" maxLength={4}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000" className="text-center text-2xl tracking-[0.5em] font-semibold"
              data-testid="otp-code-input" onKeyDown={(e) => e.key === 'Enter' && verifyOtp()} />
            <button onClick={sendOtp} disabled={sending}
              className="text-xs text-emerald-600 hover:underline" data-testid="otp-resend-btn">
              {sending ? 'Renvoi…' : 'Renvoyer le code'}
            </button>
          </div>
        )}

        <DialogFooter>
          {step === 'phone' ? (
            <Button onClick={sendOtp} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="otp-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageCircle className="w-4 h-4 mr-1" />}
              Envoyer le code
            </Button>
          ) : (
            <Button onClick={verifyOtp} disabled={verifying} className="bg-emerald-600 hover:bg-emerald-700" data-testid="otp-verify-btn">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
              Vérifier
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneOtpModal;
